import { useGetUserSpecificLogsQuery } from '@/app/features/employee/emp-dashboard/emp-dashboard.api';
import CustomTable from '@/components/CustomTable';
import useAuth from '@/hooks/useAuth';
import useFilters from '@/hooks/useFilters';
import type { TableProps } from 'antd';
import dayjs from 'dayjs';
import { useMemo } from 'react';

interface Props {
  userId?: string;
}

export default function UserSpecificLogs({ userId }: Props) {
  const { organizationId } = useAuth();

  // Use filters hook
  const { page, limit, handlePagination } = useFilters();

  // Call user specific logs API
  const {
    data: auditLogsData,
    isLoading: isLoadingAuditLog,
    isFetching,
  } = useGetUserSpecificLogsQuery(
    {
      params: {
        organization_id: organizationId as string,
        user_id: userId as string,
        page: page || 1,
        limit: limit || 10,
      },
    },
    { skip: !organizationId || !userId },
  );

  // Map audit logs data to table format
  const dataSource = useMemo(() => {
    if (!auditLogsData?.data?.data) {
      return [];
    }
    const logs = auditLogsData.data.data;
    return logs.map((log) => ({
      key: log.id,
      actionBy: log.action_by_name || 'N/A',
      action: log.log_message_admin || log.log_message || 'N/A',
      module: log.module_name || 'N/A',
      ip_address: log?.ip_address || 'N/A',
      dateTime: log.timestamp
        ? dayjs(log.timestamp).format('DD/MM/YYYY, hh:mm A')
        : 'N/A',
    }));
  }, [auditLogsData]);

  const total = auditLogsData?.data?.pagination?.total || 0;

  const columns: TableProps<any>['columns'] = [
    {
      title: 'Action By',
      dataIndex: 'actionBy',
      render: (val: string) => (
        <span className="lg:text-nowrap">{val ?? 'n/a'}</span>
      ),
    },

    {
      title: 'Action',
      dataIndex: 'action',
      render: (val) => val ?? 'n/a',
    },
    {
      width: 300,
      title: 'Module',
      dataIndex: 'module',
      render: (val) => val ?? 'n/a',
    },
    {
      width: 200,
      title: 'IP Address',
      dataIndex: 'ip_address',
      align: 'center',
      render: (val) => val ?? 'n/a',
    },
    {
      width: 200,
      title: 'Date & Time',
      dataIndex: 'dateTime',
      align: 'center',
      render: (val) => val ?? 'n/a',
    },
  ];

  return (
    <div className="bg-theme-slate-800 rounded-xl">
      <CustomTable
        page={page}
        limit={limit}
        hideRowSelect
        columns={columns}
        dataSource={dataSource}
        tableRootClassName="border-0 p-0 m-0"
        onPaginate={handlePagination}
        loading={isLoadingAuditLog || isFetching}
        total={total}
      />
    </div>
  );
}
